import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { MemoryHistory } from 'history';

import classNames from 'classnames';

import Button from 'components/common/Button';
import Image from 'components/common/Image';
import { useFocusManager } from 'hooks/focus';

import { usePageNavigation } from 'hooks/pageNavigation';
import { KitchenSinkMenuItem } from 'store-types';
import styles from './LeftNav.module.scss';

interface ExtendedMenuItem extends Omit<KitchenSinkMenuItem, 'action'> {
  action: KitchenSinkMenuItem['action'];
}

interface NavItemProps {
  item: ExtendedMenuItem | null;
  index: number | null;
  pageName?: string | null;
  isActive?: boolean;
  isFocused?: boolean;
  isExpanded?: boolean;
  class?: string | null;
}

const NavItem = ({
  item,
  index,
  pageName = null,
  isActive = false,
  isFocused = false,
  isExpanded = false,
}: NavItemProps) => {
  const history = useHistory() as MemoryHistory;
  const { navigateToHome } = usePageNavigation();
  const focusManager = useFocusManager();

  let tag: string | null | undefined = null;
  let pageType: string | null | undefined = null;
  let pathname: string | null | undefined = null;
  if (item?.action === 'tag') {
    tag = item?.tag?.key;
    pageType = item?.tag?.type?.name;
    pathname = `/${pageType}/${tag}`;
  } else if (item?.action === 'home') {
    tag = item?.action;
    pageType = item?.action;
    pathname = '/home';
  } else if (item?.action === 'settings') {
    tag = item?.action;
    pageType = item?.action;
    pathname = '/settings';
  } else if (item?.action === 'leagues') {
    tag = `all-${item?.action}`;
    pathname = '/tagrow/all-leagues';
    pageType = 'tagrow';
  } else if (item?.action === 'shows') {
    tag = `all-${item?.action}`;
    pathname = '/tagrow/all-shows';
    pageType = 'tagrow';
  } else {
    tag = item?.action;
    pageType = 'tagrow';
  }
  const onClick = useCallback(() => {
    if (tag === pageName) {
      // if the active page is selected, just send focus back to content
      focusManager.setCurrentFocus(
        `${(pageName ?? '').toLowerCase()}-focusable-content`,
      );
      return;
    }
    if (item?.action === 'home') {
      navigateToHome();
    } else if (tag) {
      history.push({
        pathname: pathname ?? undefined,
      });
    }
  }, [
    history,
    item?.action,
    navigateToHome,
    pathname,
    tag,
    pageName,
    focusManager,
  ]);

  return (
    <Button
      className={classNames(isFocused && styles['has-focus'], styles.NavItem)}
      size="md"
      focusId={`left-nav-${index}`}
      onClick={onClick}
      ttsText={item?.label ?? ''}
    >
      <Image
        alt={item?.label ?? ''}
        src={isFocused || isActive ? item?.image?.selected : item?.image?.default}
        className={classNames(styles.staticImage)}
      />
      <span
        className={classNames(
          styles.text,
          isExpanded ? '' : styles['minimized-text'],
        )}
      >
        {' '}
        {item?.label}
      </span>
    </Button>
  );
};

export default NavItem;
